import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import type { AppSearch, StorageVehicle } from '@/types';
import { queryClient } from '@/config/reactQuery';
import { QUERY_KEYS } from '../../queryKeys';
import { storage } from '../Storage';

const serializeStorageVehicleQuery = (q: AppSearch<StorageVehicle>) =>
  JSON.stringify({
    name: q.name,
    search: q.search,
    page: q.page,
    pageSize: q.pageSize,
    sortField: q.sortField,
    sortOrder: q.sortOrder,
    filters: q.filters,
  });

const prepareStorageVehicleSearchBody = (
  queryData: AppSearch<StorageVehicle>,
): AppSearch<StorageVehicle> => {
  const body: AppSearch<StorageVehicle> = JSON.parse(JSON.stringify(queryData));
  body.filters?.forEach((filter) => {
    filter.value = filter.value.map((v) =>
      v instanceof Date ? v.toISOString() : v?.toString(),
    );
  });
  return body;
};

export const useStorage = () =>
  useQuery({
    queryKey: [QUERY_KEYS.storage],
    queryFn: async () => (await storage.getStorage()).data,
  });

export const useStorageGraph = () =>
  useQuery({
    queryKey: [QUERY_KEYS.storageGraph],
    queryFn: async () => (await storage.getGraph()).data,
  });

export const useStorageVehiclesFilters = () =>
  useQuery({
    queryKey: [QUERY_KEYS.storageVehiclesFilters],
    queryFn: async () => (await storage.getStorageVehiclesFilters()).data,
  });

export const useStorageVehiclesList = (
  query: AppSearch<StorageVehicle> | undefined,
) => {
  const queryKey = useMemo(
    () =>
      [
        QUERY_KEYS.storageVehiclesList,
        query ? serializeStorageVehicleQuery(query) : '__idle__',
      ] as const,
    [query],
  );

  return useQuery({
    queryKey,
    queryFn: async () => {
      const body = prepareStorageVehicleSearchBody(query!);
      const response = await storage.postStorageVehicles(body);
      return response?.data;
    },
    enabled: !!query,
    placeholderData: keepPreviousData,
  });
};

export const fetchStorageVehiclesForExport = async (
  query: AppSearch<StorageVehicle>,
): Promise<StorageVehicle[]> => {
  const exportQuery = { ...query };
  exportQuery.page = undefined;
  exportQuery.pageSize = undefined;
  const body = prepareStorageVehicleSearchBody(
    exportQuery as AppSearch<StorageVehicle>,
  );
  const response = await storage.postStorageVehicles(body);
  return response?.data?.entities ?? [];
};

export const useCheckoutVehicles = () =>
  useMutation({
    mutationFn: storage.checkoutVehicle,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.storageVehiclesList],
      }),
  });
